import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Form, Input, Select, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { BasicFormWrapper } from 'container/styled';
import { Modal } from 'components/modals/antd-modals';
import openNotificationWithIcon from 'utility/notification';
import { CollectionChannelConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionTemplateDto,
  CollectionChannel,
  PreviewTemplateResult,
} from '../debtCollectionApi';

const { TextArea } = Input;

const PLACEHOLDERS = [
  '{{ClientName}}',
  '{{Amount}}',
  '{{RemainingAmount}}',
  '{{DueDate}}',
  '{{DaysOverdue}}',
  '{{ProjectName}}',
  '{{OrganizationName}}',
];

interface TemplateFormModalProps {
  visible: boolean;
  editMode: boolean;
  template: DebtCollectionTemplateDto | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  visible,
  editMode,
  template,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewTemplateResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<CollectionChannel>(CollectionChannel.Email);

  useEffect(() => {
    if (template && editMode) {
      form.setFieldsValue(template);
      setSelectedChannel(template.channel ?? CollectionChannel.Email);
    } else {
      form.resetFields();
      setSelectedChannel(CollectionChannel.Email);
    }
    setPreview(null);
  }, [template, editMode, form]);

  const insertPlaceholder = (placeholder: string) => {
    const currentBody = form.getFieldValue('body') || '';
    form.setFieldsValue({ body: currentBody + placeholder });
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const values = form.getFieldsValue();
      const { data } = await debtCollectionApi.previewTemplate({
        templateBody: values.body || '',
        templateSubject: values.subject,
      });
      setPreview(data);
    } catch (error) {
      console.error('Error previewing template:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (values: Partial<DebtCollectionTemplateDto>) => {
    setLoading(true);
    try {
      if (editMode && template?.id) {
        await debtCollectionApi.updateTemplate(template.id, values);
      } else {
        await debtCollectionApi.createTemplate(values);
      }
      openNotificationWithIcon(
        'success',
        t(
          `debt-collection:templates.${editMode ? 'updated' : 'created'}`,
          `Template ${editMode ? 'updated' : 'created'} successfully`,
        ),
      );
      onSuccess();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      type="primary"
      title={t(`debt-collection:templates.${editMode ? 'edit' : 'add'}`, `${editMode ? 'Edit' : 'Add'} Template`)}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <BasicFormWrapper>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t('debt-collection:templates.name', 'Name')}
            name="name"
            rules={[{ required: true, message: t('debt-collection:templates.name-required', 'Name is required') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('debt-collection:templates.channel', 'Channel')}
            name="channel"
            rules={[{ required: true }]}
          >
            <Select onChange={(val) => setSelectedChannel(val)}>
              {Object.entries(CollectionChannelConfig).map(([key, cfg]) => (
                <Select.Option key={key} value={Number(key)}>
                  {cfg.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {selectedChannel === CollectionChannel.Email && (
            <Form.Item
              label={t('debt-collection:templates.subject', 'Subject')}
              name="subject"
            >
              <Input />
            </Form.Item>
          )}

          <Form.Item
            label={t('debt-collection:templates.body', 'Body')}
            name="body"
            rules={[{ required: true, message: t('debt-collection:templates.body-required', 'Body is required') }]}
          >
            <TextArea rows={6} />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8, fontWeight: 500 }}>
              {t('debt-collection:templates.placeholders', 'Insert:')}
            </span>
            {PLACEHOLDERS.map((p) => (
              <Tag
                key={p}
                color="blue"
                style={{ cursor: 'pointer', marginBottom: 4 }}
                onClick={() => insertPlaceholder(p)}
              >
                {p}
              </Tag>
            ))}
          </div>

          <Form.Item name="isDefault" valuePropName="checked">
            <Checkbox>{t('debt-collection:templates.is-default', 'Set as default template')}</Checkbox>
          </Form.Item>

          {preview && (
            <div
              style={{
                background: '#f5f5f5',
                padding: 16,
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              <strong>{t('debt-collection:templates.preview-title', 'Preview:')}</strong>
              {preview.renderedSubject && (
                <div style={{ marginTop: 8 }}>
                  <em>{t('debt-collection:templates.subject', 'Subject')}:</em> {preview.renderedSubject}
                </div>
              )}
              <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{preview.renderedBody}</div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handlePreview} loading={previewLoading}>
              {t('debt-collection:templates.preview', 'Preview')}
            </Button>
            <Button onClick={onCancel}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode ? t('global:update', 'Update') : t('global:create', 'Create')}
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};

export default TemplateFormModal;
